import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "constants/index";

type Props = {
  children: ReactNode;
  fallbackTitle?: string;
};

type State = {
  hasError: boolean;
  message?: string;
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, message: undefined });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-[50vh] flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-xl font-semibold text-secondary-900">
            {this.props.fallbackTitle ?? "Something went wrong"}
          </h1>
          <p className="text-sm text-secondary-600">
            An unexpected error occurred. You can try again or return home.
          </p>
          {import.meta.env.DEV && this.state.message && (
            <pre className="text-left text-xs bg-secondary-50 border border-secondary-200 rounded-lg p-3 overflow-auto text-red-700">
              {this.state.message}
            </pre>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <button
              type="button"
              onClick={this.handleRetry}
              className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
            >
              Reload page
            </button>
            <Link
              to={ROUTES.HOME}
              className="px-4 py-2 rounded-lg border border-secondary-300 text-sm font-medium text-secondary-800 hover:bg-secondary-50"
            >
              Go home
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
